import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import adminService from '../../services/adminService';
import { format } from 'date-fns';

import backIcon from '../../assets/icons/indicators/back_icon.svg'; // adjust the filename

const AdminJournalDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    loadJournalEntry();
  }, [id]);

  const loadJournalEntry = async () => {
  try {
    setLoading(true);
    setError(null);
    
    const response = await adminService.getJournalEntryById(id);
    
    if (response.success) {
      console.log('📊 Journal entry data:', response.data);
      console.log('📅 Available date fields:', {
        date: response.data.date,
        createdAt: response.data.createdAt,
        timestamp: response.data.timestamp,
        updatedAt: response.data.updatedAt
      });
      setEntry(response.data);
    } else {
      throw new Error(response.message || 'Failed to load journal entry');
    }
  } catch (err) {
    console.error('Error loading journal entry:', err);
    setError(err.message);
      
      // Mock data for development
      if (process.env.NODE_ENV === 'development') {
        setEntry({
          _id: id,
          title: 'Today\'s Reflection',
          content: 'Today I realized how much progress I\'ve made in managing my anxiety. The breathing exercises Dr. Wilson taught me have been incredibly helpful. I noticed that when I start to feel overwhelmed, taking just five minutes to focus on my breath makes a significant difference.\n\nI also had a good conversation with my colleague about the project deadline. Instead of internalizing my stress, I communicated my concerns openly, and we came up with a solution together. This is a big step for me.\n\nThings I\'m grateful for today:\n- My supportive team at work\n- The beautiful weather\n- My morning coffee routine\n- Making progress in therapy',
          patientName: 'John Doe',
          patientId: '1',
          doctorName: 'Dr. Sarah Wilson',
          doctorId: '1',
          tenantName: 'MindBridge Health',
          tenantId: '1',
          date: new Date().toISOString(),
          sentiment: { 
            type: 'positive', 
            score: 0.8,
            analysis: 'The entry reflects significant positive progress and self-awareness. The patient demonstrates healthy coping mechanisms and improved communication skills.'
          },
          mood: { 
            label: 'good',
            description: 'Feeling positive and accomplished'
          },
          emotions: ['happiness', 'gratitude', 'relief', 'confidence'],
          wordCount: 156,
          readingTime: 1,
          tags: ['anxiety', 'progress', 'gratitude', 'communication'],
          isSharedWithDoctor: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await adminService.deleteJournalEntry(id);
      
      if (response.success) {
        toast.success('Journal entry deleted successfully');
        navigate('/admin/journal-entries');
      } else {
        throw new Error(response.message || 'Failed to delete journal entry');
      }
    } catch (err) {
      console.error('Error deleting journal entry:', err);
      toast.error(err.message);
    }
  };

  const handleExportPDF = async () => {
    try {
      await adminService.exportSingleJournalEntryToPDF(id);
      toast.success('PDF exported successfully');
    } catch (err) {
      console.error('Error exporting PDF:', err);
      toast.error('Failed to export PDF');
    }
  };

  const formatDateSafe = (dateString) => {
    try {
      return format(new Date(dateString), 'MMMM dd, yyyy \'at\' h:mm a');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getSentimentColor = (sentiment) => {
    const type = sentiment?.type || 'neutral';
    switch (type) {
      case 'positive': return '#4CAF50';
      case 'negative': return '#F44336';
      default: return '#FFD700';
    }
  };

  const getSentimentBgColor = (sentiment) => {
    const type = sentiment?.type || 'neutral';
    switch (type) {
      case 'positive': return 'rgba(76, 175, 80, 0.1)';
      case 'negative': return 'rgba(244, 67, 54, 0.1)';
      default: return 'rgba(255, 215, 0, 0.1)';
    }
  };

  const getMoodEmoji = (mood) => {
    const label = mood?.label || 'neutral';
    switch (label) {
      case 'good': return '😊';
      case 'struggling': return '😟';
      case 'neutral': return '😐';
      default: return '😐';
    }
  };

  const getMoodText = (mood) => {
    const label = mood?.label || 'neutral';
    switch (label) {
      case 'good': return "I'm good";
      case 'struggling': return "I'm struggling";
      case 'neutral': return "I'm okay";
      default: return "I'm okay";
    }
  };

  const styles = {
    container: {
      padding: '40px 24px 24px 24px',
      backgroundColor: '#FFFFFF',
      minHeight: '100vh'
    },
    header: {
      marginBottom: '32px'
    },
    headerRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      marginBottom: '8px'
    },
    backButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0',  // Remove padding since we have fixed size
      width: '70px',
      height: '70px',
      backgroundColor: 'transparent',
      border: '1px solid #A7D7C5',
      borderRadius: '8px',
      color: '#548170',
      fontSize: '15px',
      fontFamily: 'IBM Plex Mono, monospace',
      cursor: 'pointer',
      flexShrink: 0,
      alignSelf: 'flex-start'
    },
    headerTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '16px'
    },
    headerTitle: {
      fontSize: '45px',
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 400,
      lineHeight: '50px',
      color: '#000000',
      margin: 0,
      flexShrink: 0
    },
    actionButtons: {
      display: 'flex',
      gap: '12px'
    },
    exportButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '12px 20px',
      backgroundColor: '#548170',
      border: '1px solid #548170',
      borderRadius: '10px',
      color: '#FFFFFF',
      fontSize: '15px',
      fontFamily: 'IBM Plex Mono, monospace',
      fontWeight: 700,
      cursor: 'pointer'
    },
    deleteButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '12px 20px',
      backgroundColor: 'transparent',
      border: '1px solid #F44336',
      borderRadius: '10px',
      color: '#F44336',
      fontSize: '15px',
      fontFamily: 'IBM Plex Mono, monospace',
      fontWeight: 700,
      cursor: 'pointer'
    },
    headerSubtitle: {
      fontSize: '15px',
      fontFamily: 'IBM Plex Mono, monospace',
      fontStyle: 'italic',
      fontWeight: 400,
      lineHeight: '50px',
      color: '#3D6B59',
      margin: 0,
      whiteSpace: 'nowrap'
    },
    dividerLine: {
      width: '100%',
      height: '1px',
      border: 'none',
      backgroundColor: '#A7D7C5',
      margin: '24px 0'
    },
    contentGrid: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      marginTop: '24px',
      width: '100%',
    },
    mainContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px'
    },
    sidebar: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px'
    },
    card: {
      backgroundColor: '#F6FBF9',
      border: '1px solid #A7D7C5',
      borderRadius: '12px',
      width: '100%',
      padding: '24px',
      flex: 1,  // This makes it grow to fill available space
      minHeight: '700px'
    },
    cardTitle: {
      fontSize: '20px',
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 600,
      color: '#000000',
      marginBottom: '16px'
    },
    infoRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 0',
      borderBottom: '1px solid rgba(167, 215, 197, 0.3)'
    },
    infoLabel: {
      fontSize: '14px',
      fontFamily: 'IBM Plex Mono, monospace',
      color: '#666',
      fontWeight: 600
    },
    infoValue: {
      fontSize: '15px',
      fontFamily: 'IBM Plex Mono, monospace',
      color: '#333'
    },
    journalContent: {
      fontSize: '16px',
      fontFamily: 'IBM Plex Mono, monospace',
      lineHeight: '28px',
      color: '#333',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word'
    },
    moodCard: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      padding: '20px',
      backgroundColor: 'rgba(167, 215, 197, 0.1)',
      borderRadius: '12px',
      border: '1px solid #A7D7C5'
    },
    moodEmoji: {
      fontSize: '48px'
    },
    moodText: {
      fontSize: '18px',
      fontFamily: 'IBM Plex Mono, monospace',
      fontWeight: 600,
      color: '#333'
    },
    sentimentCard: {
      padding: '20px',
      borderRadius: '12px',
      border: '1px solid #A7D7C5'
    },
    sentimentHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '12px'
    },
    sentimentDot: {
      width: '16px',
      height: '16px',
      borderRadius: '50%'
    },
    sentimentLabel: {
      fontSize: '18px',
      fontFamily: 'IBM Plex Mono, monospace',
      fontWeight: 600,
      color: '#333'
    },
    sentimentScore: {
      fontSize: '14px',
      fontFamily: 'IBM Plex Mono, monospace',
      color: '#666',
      marginBottom: '12px'
    },
    sentimentAnalysis: {
      fontSize: '14px',
      fontFamily: 'IBM Plex Mono, monospace',
      lineHeight: '22px',
      color: '#333',
      fontStyle: 'italic'
    },
    emotionTags: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      marginTop: '12px'
    },
    emotionTag: {
      display: 'inline-block',
      padding: '6px 14px',
      borderRadius: '20px',
      backgroundColor: 'rgba(167, 215, 197, 0.3)',
      color: '#333',
      fontSize: '14px',
      fontFamily: 'IBM Plex Mono, monospace',
      fontWeight: 500
    },
    tags: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px'
    },
    tag: {
      display: 'inline-block',
      padding: '6px 14px',
      borderRadius: '20px',
      backgroundColor: 'rgba(84, 129, 112, 0.1)',
      color: '#548170',
      fontSize: '13px',
      fontFamily: 'IBM Plex Mono, monospace',
      border: '1px solid rgba(84, 129, 112, 0.3)'
    },
    statBox: {
      textAlign: 'center',
      padding: '16px',
      backgroundColor: 'rgba(167, 215, 197, 0.1)',
      borderRadius: '10px'
    },
    statValue: {
      fontSize: '32px',
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 600,
      color: '#548170',
      marginBottom: '4px'
    },
    statLabel: {
      fontSize: '14px',
      fontFamily: 'IBM Plex Mono, monospace',
      color: '#666'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modalContent: {
      backgroundColor: '#fff',
      borderRadius: '12px',
      padding: '24px',
      maxWidth: '500px',
      width: '90%'
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    modalTitle: {
      fontSize: '24px',
      fontWeight: 600,
      color: '#333'
    },
    modalClose: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: '#666'
    },
    modalActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '12px',
      marginTop: '24px'
    },
    btnSecondary: {
      padding: '10px 20px',
      backgroundColor: '#fff',
      border: '1px solid #A7D7C5',
      borderRadius: '8px',
      color: '#548170',
      cursor: 'pointer',
      fontFamily: 'IBM Plex Mono, monospace'
    },
    btnDanger: {
      padding: '10px 20px',
      backgroundColor: '#F44336',
      border: '1px solid #F44336',
      borderRadius: '8px',
      color: '#fff',
      cursor: 'pointer',
      fontFamily: 'IBM Plex Mono, monospace'
    },
    sharedBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 12px',
      backgroundColor: 'rgba(76, 175, 80, 0.1)',
      border: '1px solid rgba(76, 175, 80, 0.3)',
      borderRadius: '16px',
      color: '#4CAF50',
      fontSize: '13px',
      fontFamily: 'IBM Plex Mono, monospace',
      fontWeight: 600
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <div style={{ 
            width: '50px', 
            height: '50px', 
            border: '4px solid #f3f3f3', 
            borderTop: '4px solid #548170', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite' 
          }}></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <button style={styles.backButton} onClick={() => navigate('/admin/journal-entries')}>
            ← Back to Journal Entries
          </button>
          <h1 style={styles.headerTitle}>Journal Entry</h1>
        </div>
        <div style={{ 
          backgroundColor: '#FFEBEE', 
          borderLeft: '4px solid #F44336', 
          color: '#D32F2F', 
          padding: '20px', 
          borderRadius: '4px',
          marginTop: '20px'
        }}>
          <span style={{ fontWeight: 600, marginRight: '8px' }}>Error:</span> {error}
        </div>
      </div>
    );
  }

  if (!entry) {
    return (
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <button style={styles.backButton} onClick={() => navigate('/admin/journal-entries')}>
            ← Back to Journal Entries
          </button>
          <h1 style={styles.headerTitle}>Journal Entry</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <h3>Journal entry not found</h3>
        </div>
      </div>
    );
  }

  const emotions = Array.isArray(entry.emotions) ? entry.emotions : [];
  const tags = Array.isArray(entry.tags) ? entry.tags : [];
  const sentimentType = entry.sentiment?.type || 'neutral';
  const sentimentLabel = sentimentType.charAt(0).toUpperCase() + sentimentType.slice(1);


    return (
  <div style={styles.container}>
    {/* Header */}
    <div style={styles.header}>
      <div style={styles.headerRow}>
        <button style={styles.backButton} onClick={() => navigate('/admin/journal-entries')}>
          <img src={backIcon} alt="Back" style={{ width: '30px', height: '30px' }} />
        </button>
        <div>
          <h1 style={styles.headerTitle}>Journal Entry</h1>
          <p style={styles.headerSubtitle}>
            {formatDateSafe(entry.createdAt || entry.date || entry.timestamp || entry.updatedAt)}
          </p>
        </div>
      </div>
    </div>

    <hr style={styles.dividerLine} />

      {/* Content Grid */}
      <div style={styles.contentGrid}>
        {/* Main Content */}
        <div style={styles.mainContent}>
          {/* Journal Content Card */}
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Journal Entry</h2>
            <div style={styles.journalContent}>
              {entry.rawText || entry.content || 'No content available'}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={styles.modal} onClick={() => setShowDeleteModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Confirm Deletion</h2>
              <button 
                style={styles.modalClose}
                onClick={() => setShowDeleteModal(false)}
              >
                ×
              </button>
            </div>
            
            <div>
              <p>Are you sure you want to permanently delete this journal entry?</p>
              <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <strong>Entry:</strong> {entry.title || 'Journal Entry'}<br />
                <strong>Patient:</strong> {entry.patientName}<br />
                <strong>Date:</strong> {formatDateSafe(entry.date)}<br />
                <strong>Words:</strong> {entry.wordCount || 0}
              </div>
              <p style={{ color: '#F44336', marginTop: '16px' }}>
                ⚠️ This action cannot be undone.
              </p>
            </div>
            
            <div style={styles.modalActions}>
              <button 
                style={styles.btnSecondary}
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button 
                style={styles.btnDanger}
                onClick={handleDelete}
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminJournalDetail;